import cx from "classnames";
import { Fragment, ReactNode, useState } from "react";

import type { ListingTypeOptions } from "@/utils/productTypes";
import { components } from "api/generated";
import { Models } from "api/types";
import DownChevron from "atoms/icon/icons/chevron_down_small.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { ModalSelectorFilterList } from "organisms/selection-modals/modal-selector-filter-list/ModalSelectorFilterList";
import { useListingFilterAndSort } from "pages/root/shop/useListingFilterAndSort";

interface Props {
  listings: ShopListingModel[];
  types: ListingTypeOptions[];
  concerns: Models<"ConcernModel">[];
  areas: Models<"AreaResult">[];
  children: (listing: ShopListingModel, index: number) => ReactNode;
}

export const typeOptions = new Map([
  ["Package", "Packages"],
  ["Treatment", "Treatments"],
  ["Membership", "Memberships"],
  ["Product", "Products"],
  ["ProductFromExternalSource", "Products"],
  ["GiftCard", "Gift Cards"],
] as const);

export interface ShopListingModel {
  areaIds: string[];
  baseCost: number;
  concernIds: string[];
  created: string;
  customColor?: string | null;
  description: string;
  discountText?: string | null;
  id: string;
  imageUrl?: string | null;
  membershipCost?: number | null;
  packages?: components["schemas"]["PackageModel"][] | null;
  packageUnitType?: components["schemas"]["PackageUnitTypeModel"] | null;
  sellVolume: number;
  title: string;
  type: ListingTypeOptions;
  benefits?: components["schemas"]["MembershipBenefitModel"][];
  discount?: Models<"ShopProductDiscountModel"> | null;
  priceRange?: string | null;
}

export const sortOptions = new Map([
  ["featured", "Featured"],
  ["bestsellers", "Best sellers"],
  ["onsale", "On sale"],
  ["priceLowToHigh", "Price - Low to high"],
  ["priceHighToLow", "Price - High to low"],
  ["recentlyAdded", "Recently added"],
] as const);

export type ListingSortOptions = Parameters<(typeof sortOptions)["get"]>[0];

export const FilterAndSortListings = ({
  listings,
  types,
  concerns,
  areas,
  children: renderItem,
}: Props) => {
  const type = types.length === 1 ? types[0] : null;
  const filterAndSortState = useListingFilterAndSort(listings, type);

  const [activeModal, setActiveModal] = useState<
    "concern" | "area" | "type" | "sort" | null
  >(null);

  const filterLabel = (value: string, count: number) => {
    if (count <= 1) {
      return value;
    } else {
      return `${value} +${count - 1}`;
    }
  };

  const typesFromListings = [
    ...new Set(listings.map((listing) => listing.type)),
  ];

  const showTreatmentTypeFilter =
    types.length > 1 && typesFromListings.length > 1;

  return (
    <div className="flex flex-col px-4 py-2">
      <div
        className={cx(
          "flex items-center overflow-x-auto",
          (showTreatmentTypeFilter || concerns.length > 0) &&
            "justify-between gap-2"
        )}
      >
        <div className="flex flex-row items-center justify-start">
          {(showTreatmentTypeFilter || concerns.length > 0) && (
            <div className="text-body2 text-primary">Filter:</div>
          )}
          {showTreatmentTypeFilter && (
            <TextButton
              endIconSVG={DownChevron}
              onClick={() => {
                setActiveModal("type");
                filterAndSortState.saveCheckpoints();
              }}
            >
              <div className="text-bold2 text-primary">
                {filterAndSortState.type ? (
                  typeOptions.get(filterAndSortState.type)
                ) : (
                  <>Service&nbsp;Type</>
                )}
              </div>
            </TextButton>
          )}
          {concerns.length > 0 && (
            <TextButton
              endIconSVG={DownChevron}
              onClick={() => {
                setActiveModal("concern");
                filterAndSortState.saveCheckpoints();
              }}
            >
              <div className="text-bold2 text-primary">
                {filterLabel(
                  concerns.find(
                    ({ id }) => id === filterAndSortState.concerns[0]
                  )?.name ?? "Concern",
                  filterAndSortState.concerns.length
                )}
              </div>
            </TextButton>
          )}
          {areas.length > 0 && (
            <TextButton
              endIconSVG={DownChevron}
              onClick={() => {
                setActiveModal("area");
                filterAndSortState.saveCheckpoints();
              }}
            >
              <div className="text-bold2 text-primary">
                {filterLabel(
                  areas.find(({ id }) => id === filterAndSortState.areas[0])
                    ?.name ?? "Area",
                  filterAndSortState.areas.length
                )}
              </div>
            </TextButton>
          )}
        </div>
        <div className="flex flex-row items-center">
          <div className="text-body2 text-primary">Sort:</div>
          <TextButton
            onClick={() => {
              setActiveModal("sort");
              filterAndSortState.saveCheckpoints();
            }}
          >
            <div className="whitespace-nowrap text-bold2 text-primary">
              {sortOptions.get(filterAndSortState.sort)}
            </div>
          </TextButton>
        </div>
        {activeModal === "type" && (
          <ModalSelectorFilterList
            open={activeModal === "type"}
            onClose={() => {
              setActiveModal(null);
              filterAndSortState.restore();
            }}
            title="FILTER BY SERVICE TYPE"
            type="filter"
            items={Array.from(typeOptions).map(([id, title]) => ({
              id,
              title,
              disabled: (filterAndSortState.typeCount[id] ?? 0) === 0,
            }))}
            checkedItems={filterAndSortState.type ?? []}
            onChange={(id) => {
              if (id == filterAndSortState.type) {
                filterAndSortState.setType(null);
              } else {
                filterAndSortState.setType(id as ListingTypeOptions);
              }
            }}
            firstButtonLabel="Apply"
            onFirstButtonClick={() => {
              setActiveModal(null);
              filterAndSortState.setSearchParams();
            }}
            firstButtonDisabled={filterAndSortState.type == null}
            secondButtonLabel="Reset filter"
            onSecondButtonClick={() => {
              setActiveModal(null);
              filterAndSortState.setType(null);
            }}
          />
        )}
        {activeModal === "concern" && (
          <ModalSelectorFilterList
            open={activeModal === "concern"}
            onClose={() => {
              setActiveModal(null);
              filterAndSortState.restore();
            }}
            title="FILTER BY CONCERN"
            type="filter"
            items={concerns.map(({ id, name }) => ({
              id,
              title: name ?? "",
              disabled: (filterAndSortState.concernsCount[id] ?? 0) === 0,
            }))}
            checkedItems={filterAndSortState.concerns}
            onChange={filterAndSortState.setConcerns}
            firstButtonLabel="Apply"
            onFirstButtonClick={() => {
              setActiveModal(null);
              filterAndSortState.setSearchParams();
            }}
            firstButtonDisabled={!filterAndSortState.concerns.length}
            secondButtonLabel="Reset filter"
            onSecondButtonClick={() => {
              setActiveModal(null);
              filterAndSortState.clearConcerns();
            }}
          />
        )}

        {activeModal === "area" && (
          <ModalSelectorFilterList
            open={activeModal === "area"}
            onClose={() => {
              setActiveModal(null);
              filterAndSortState.restore();
            }}
            title="FILTER BY AREA"
            type="filter"
            items={areas.map(({ id, name }) => ({
              id,
              title: name ?? "",
              disabled: (filterAndSortState.areasCount[id] ?? 0) === 0,
            }))}
            checkedItems={filterAndSortState.areas}
            onChange={filterAndSortState.setAreas}
            firstButtonLabel="Apply"
            onFirstButtonClick={() => {
              setActiveModal(null);
              filterAndSortState.setSearchParams();
            }}
            firstButtonDisabled={!filterAndSortState.areas.length}
            secondButtonLabel="Reset filter"
            onSecondButtonClick={() => {
              setActiveModal(null);
              filterAndSortState.clearAreas();
            }}
          />
        )}

        {activeModal === "sort" && (
          <ModalSelectorFilterList
            open={activeModal === "sort"}
            onClose={() => {
              setActiveModal(null);
              filterAndSortState.restore();
            }}
            title="SORT BY"
            type="sort"
            items={Array.from(filterAndSortState.sortOptions).map(
              ([id, title]) => {
                return {
                  id,
                  title,
                };
              }
            )}
            checkedItems={filterAndSortState.sort}
            onChange={(id: string) => {
              filterAndSortState.sortBy(id as ListingSortOptions);
            }}
            firstButtonLabel="Apply"
            onFirstButtonClick={() => {
              setActiveModal(null);
              filterAndSortState.setSearchParams();
            }}
          />
        )}
      </div>

      <div className="flex flex-col gap-4">
        {filterAndSortState.filteredListings.map((item, index) => (
          <Fragment key={item.id}>{renderItem(item, index)}</Fragment>
        ))}
      </div>
    </div>
  );
};
